import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { ReviewService } from '../../core/services/review.service';
import { ReviewItem, ReviewStats } from '../../models/review.interface';

@Component({
  selector: 'app-review-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SidebarComponent, NavbarComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar
      pageTitle="Product Reviews & Ratings"
      pageSubtitle="Moderate customer feedback, ratings and verify authenticity"
    ></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">

        <!-- TOP METRICS STATS -->
        <section class="stats-row">
          <div class="stat-card glass-card">
            <div class="icon-box icon-box-gold">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </div>
            <div class="stat-info">
              <div class="stat-num text-gold">{{ stats.totalReviews }}</div>
              <div class="stat-lbl">Total Reviews</div>
            </div>
          </div>

          <div class="stat-card glass-card pending-card" [class.has-pending]="stats.pendingCount > 0">
            <div class="icon-box icon-box-yellow">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div class="stat-info">
              <div class="stat-num text-yellow">{{ stats.pendingCount }}</div>
              <div class="stat-lbl">Pending Approval</div>
            </div>
          </div>

          <div class="stat-card glass-card">
            <div class="icon-box icon-box-green">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div class="stat-info">
              <div class="stat-num text-green">{{ stats.approvedCount }}</div>
              <div class="stat-lbl">Live on Storefront</div>
            </div>
          </div>

          <div class="stat-card glass-card">
            <div class="icon-box icon-box-blue">
              <span style="font-size: 16px; font-weight: 800;">★</span>
            </div>
            <div class="stat-info">
              <div class="stat-num text-blue">{{ stats.averageRating }} / 5</div>
              <div class="stat-lbl">Average Rating</div>
            </div>
          </div>
        </section>

        <!-- FILTERS & SEARCH -->
        <section class="glass-card controls-card">
          <div class="controls-grid">
            <!-- Search -->
            <div class="search-box">
              <input
                type="text"
                class="form-control"
                placeholder="Search reviews, customer name, email, product..."
                [(ngModel)]="searchQuery"
                (ngModelChange)="onSearchChange()"
              />
            </div>

            <!-- Moderation Tabs -->
            <div class="filter-tabs">
              <button
                class="tab-btn"
                [class.active]="selectedStatus === 'all'"
                (click)="setStatusFilter('all')"
              >
                All ({{ stats.totalReviews }})
              </button>

              <button
                class="tab-btn pending-tab"
                [class.active]="selectedStatus === 'pending'"
                (click)="setStatusFilter('pending')"
              >
                ⏳ Pending
                <span class="tab-badge" *ngIf="stats.pendingCount > 0">{{ stats.pendingCount }}</span>
              </button>

              <button
                class="tab-btn"
                [class.active]="selectedStatus === 'approved'"
                (click)="setStatusFilter('approved')"
              >
                ✅ Approved ({{ stats.approvedCount }})
              </button>

              <button
                class="tab-btn"
                [class.active]="selectedStatus === 'rejected'"
                (click)="setStatusFilter('rejected')"
              >
                ❌ Rejected ({{ stats.rejectedCount }})
              </button>
            </div>
          </div>
        </section>

        <!-- LOADING STATE -->
        <div *ngIf="loading" class="state-box">
          <div class="loading-spinner"></div>
          <p>Loading reviews...</p>
        </div>

        <!-- REVIEWS FEED -->
        <div class="reviews-feed" *ngIf="!loading && reviews.length > 0">
          <div *ngFor="let review of reviews" class="review-admin-card glass-card">

            <!-- Card Header: Product & Customer & Star Rating -->
            <div class="review-card-head">
              <!-- Left: Product info -->
              <div class="product-info-block">
                <div class="product-thumb">
                  <img *ngIf="review.productImage" [src]="review.productImage" [alt]="review.productTitle" />
                  <div *ngIf="!review.productImage" class="thumb-placeholder">SM</div>
                </div>
                <div>
                  <h4 class="product-title">{{ review.productTitle || 'Custom Phone Case' }}</h4>
                  <div class="customer-sub">
                    <strong>{{ review.name }}</strong>
                    <span class="sub-sep">·</span>
                    <a [href]="'mailto:' + review.email" class="email-link">{{ review.email }}</a>
                    <span *ngIf="review.deviceModel" class="device-pill">📱 {{ review.deviceModel }}</span>
                    <span *ngIf="review.isVerifiedPurchase" class="verified-badge">
                      ✓ Verified Buyer
                    </span>
                    <span *ngIf="review.orderNumber" class="order-pill">
                      #{{ review.orderNumber }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Right: Stars & Status Badge -->
              <div class="status-stars-block">
                <div class="star-rating-box">
                  <span
                    *ngFor="let s of [1, 2, 3, 4, 5]"
                    class="star-icon"
                    [class.filled]="s <= review.rating"
                  >★</span>
                  <span class="rating-num">({{ review.rating }}/5)</span>
                </div>

                <div class="status-indicator">
                  <span [class]="getStatusClass(review.status)">
                    {{ getStatusLabel(review.status) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Review Content Body -->
            <div class="review-body">
              <h5 class="review-headline" *ngIf="review.title">{{ review.title }}</h5>
              <p class="review-text">&ldquo;{{ review.comment }}&rdquo;</p>
              <div class="review-date">
                Submitted on {{ review.createdAt | date:'dd MMM yyyy, hh:mm a' }}
              </div>
            </div>

            <!-- Card Footer: Actions -->
            <div class="review-card-footer">
              <div class="footer-notice">
                <span *ngIf="review.isApproved" class="text-green">
                  🟢 Currently Visible to all website visitors on product page
                </span>
                <span *ngIf="!review.isApproved && review.status === 'pending'" class="text-yellow">
                  🟡 Hidden from website visitors (Awaiting Admin Approval)
                </span>
                <span *ngIf="review.status === 'rejected'" class="text-red">
                  🔴 Rejected (Not visible on website)
                </span>
              </div>

              <div class="action-buttons-group">
                <!-- Approve Button -->
                <button
                  *ngIf="review.status !== 'approved'"
                  class="btn-approve"
                  (click)="updateStatus(review, 'approved')"
                  [disabled]="actionLoadingId === review._id"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span>Approve &amp; Publish</span>
                </button>

                <!-- Reject Button -->
                <button
                  *ngIf="review.status !== 'rejected'"
                  class="btn-reject"
                  (click)="updateStatus(review, 'rejected')"
                  [disabled]="actionLoadingId === review._id"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  <span>Reject</span>
                </button>

                <!-- Delete Button -->
                <button
                  class="btn-delete"
                  (click)="deleteReview(review)"
                  [disabled]="actionLoadingId === review._id"
                  title="Permanently Delete Review"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  </svg>
                </button>
              </div>
            </div>

          </div>
        </div>

        <!-- EMPTY STATE -->
        <div *ngIf="!loading && reviews.length === 0" class="glass-card empty-card">
          <div class="empty-icon">⭐</div>
          <h3 class="font-serif">No Reviews Found</h3>
          <p>
            {{ selectedStatus === 'pending'
              ? 'All caught up! There are no pending reviews awaiting approval.'
              : 'No product reviews match the current filter.' }}
          </p>
        </div>

      </main>
    </div>
  `,
  styles: [`
    .main-content {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    /* Stats Row */
    .stats-row {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;
    }

    .stat-card {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 20px;
    }

    .icon-box {
      width: 44px;
      height: 44px;
      border-radius: var(--radius-md);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .icon-box-gold { background: var(--accent-gold-glow); color: var(--accent-gold); border: 1px solid var(--border-highlight); }
    .icon-box-yellow { background: rgba(251, 191, 36, 0.12); color: #fbbf24; border: 1px solid rgba(251, 191, 36, 0.25); }
    .icon-box-green { background: rgba(34, 197, 94, 0.12); color: #22c55e; border: 1px solid rgba(34, 197, 94, 0.25); }
    .icon-box-blue { background: rgba(59, 130, 246, 0.12); color: #60a5fa; border: 1px solid rgba(59, 130, 246, 0.25); }

    .stat-info { display: flex; flex-direction: column; gap: 2px; }
    .stat-num { font-size: 24px; font-weight: 800; font-family: var(--font-sans); }
    .stat-lbl { font-size: 12px; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.05em; }

    .pending-card.has-pending {
      border-color: rgba(251, 191, 36, 0.45);
      box-shadow: 0 0 20px rgba(251, 191, 36, 0.15);
    }

    /* Controls */
    .controls-card {
      padding: 16px 20px;
    }

    .controls-grid {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 14px;
    }

    .search-box {
      flex: 1;
      min-width: 260px;
    }

    .filter-tabs {
      display: flex;
      align-items: center;
      gap: 6px;
      background: rgba(255, 255, 255, 0.04);
      padding: 4px;
      border-radius: var(--radius-md);
      border: 1px solid var(--border-color);
      flex-wrap: wrap;
    }

    .tab-btn {
      background: transparent;
      border: none;
      color: var(--text-secondary);
      font-size: 12px;
      font-weight: 600;
      padding: 7px 14px;
      border-radius: var(--radius-sm);
      cursor: pointer;
      transition: all 0.2s ease;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    .tab-btn:hover { color: var(--text-primary); background: rgba(255, 255, 255, 0.05); }
    .tab-btn.active {
      background: rgba(201, 169, 110, 0.2);
      color: var(--accent-gold);
      border: 1px solid var(--border-highlight);
      font-weight: 700;
    }
    .tab-btn.pending-tab.active {
      background: rgba(251, 191, 36, 0.18);
      color: #fbbf24;
      border-color: rgba(251, 191, 36, 0.35);
    }
    .tab-badge {
      background: #fbbf24;
      color: #000;
      font-size: 10px;
      font-weight: 800;
      padding: 1px 6px;
      border-radius: 999px;
    }

    /* Review Cards Feed */
    .reviews-feed {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .review-admin-card {
      padding: 22px;
      display: flex;
      flex-direction: column;
      gap: 14px;
      border-left: 4px solid var(--border-color);
      transition: all 0.2s ease;
    }
    .review-admin-card:hover {
      border-color: var(--border-highlight);
    }

    .review-card-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 14px;
    }

    .product-info-block {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .product-thumb {
      width: 48px;
      height: 48px;
      border-radius: var(--radius-md);
      overflow: hidden;
      background: #111;
      border: 1px solid var(--border-color);
      flex-shrink: 0;
    }
    .product-thumb img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .thumb-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--accent-gold);
      font-weight: 800;
      font-size: 13px;
    }

    .product-title {
      font-size: 15px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0 0 4px;
    }

    .customer-sub {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: var(--text-secondary);
      flex-wrap: wrap;
    }
    .sub-sep { color: var(--border-color); }
    .email-link { color: var(--text-muted); text-decoration: none; }
    .email-link:hover { color: var(--accent-gold); }

    .device-pill, .order-pill {
      font-size: 10.5px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid var(--border-color);
      padding: 1px 7px;
      border-radius: 4px;
      color: var(--text-secondary);
    }
    .verified-badge {
      font-size: 10.5px;
      font-weight: 700;
      background: rgba(34, 197, 94, 0.12);
      border: 1px solid rgba(34, 197, 94, 0.3);
      color: #22c55e;
      padding: 1px 7px;
      border-radius: 4px;
    }

    .status-stars-block {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 6px;
    }

    .star-rating-box {
      display: flex;
      align-items: center;
      gap: 2px;
    }
    .star-icon {
      font-size: 16px;
      color: rgba(255, 255, 255, 0.2);
    }
    .star-icon.filled {
      color: #F59E0B;
    }
    .rating-num {
      font-size: 11px;
      color: var(--text-muted);
      margin-left: 4px;
      font-weight: 700;
    }

    /* Badges */
    .badge-pending {
      background: rgba(251, 191, 36, 0.15);
      color: #fbbf24;
      border: 1px solid rgba(251, 191, 36, 0.35);
      font-size: 10.5px;
      font-weight: 800;
      padding: 3px 9px;
      border-radius: var(--radius-full);
      letter-spacing: 0.05em;
    }
    .badge-approved {
      background: rgba(34, 197, 94, 0.15);
      color: #22c55e;
      border: 1px solid rgba(34, 197, 94, 0.35);
      font-size: 10.5px;
      font-weight: 800;
      padding: 3px 9px;
      border-radius: var(--radius-full);
      letter-spacing: 0.05em;
    }
    .badge-rejected {
      background: rgba(239, 68, 68, 0.15);
      color: #ef4444;
      border: 1px solid rgba(239, 68, 68, 0.35);
      font-size: 10.5px;
      font-weight: 800;
      padding: 3px 9px;
      border-radius: var(--radius-full);
      letter-spacing: 0.05em;
    }

    /* Review Body */
    .review-body {
      background: rgba(0, 0, 0, 0.2);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      padding: 14px 16px;
    }
    .review-headline {
      font-size: 13.5px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0 0 6px;
    }
    .review-text {
      font-size: 13px;
      color: var(--text-secondary);
      line-height: 1.55;
      margin: 0 0 8px;
    }
    .review-date {
      font-size: 11px;
      color: var(--text-muted);
    }

    /* Footer Actions */
    .review-card-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 12px;
      padding-top: 6px;
    }
    .footer-notice {
      font-size: 11.5px;
      font-weight: 600;
    }
    .text-green { color: #22c55e; }
    .text-yellow { color: #fbbf24; }
    .text-red { color: #ef4444; }

    .action-buttons-group {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .btn-approve {
      background: rgba(34, 197, 94, 0.15);
      border: 1px solid rgba(34, 197, 94, 0.4);
      color: #22c55e;
      font-size: 12px;
      font-weight: 700;
      padding: 6px 14px;
      border-radius: var(--radius-sm);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: all 0.2s ease;
    }
    .btn-approve:hover:not(:disabled) {
      background: #22c55e;
      color: #FFF;
      box-shadow: 0 2px 10px rgba(34, 197, 94, 0.35);
    }

    .btn-reject {
      background: rgba(239, 68, 68, 0.12);
      border: 1px solid rgba(239, 68, 68, 0.3);
      color: #ef4444;
      font-size: 12px;
      font-weight: 700;
      padding: 6px 14px;
      border-radius: var(--radius-sm);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: all 0.2s ease;
    }
    .btn-reject:hover:not(:disabled) {
      background: #ef4444;
      color: #FFF;
      box-shadow: 0 2px 10px rgba(239, 68, 68, 0.35);
    }

    .btn-delete {
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid var(--border-color);
      color: var(--text-muted);
      padding: 6px 10px;
      border-radius: var(--radius-sm);
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .btn-delete:hover:not(:disabled) {
      background: rgba(239, 68, 68, 0.2);
      border-color: #ef4444;
      color: #ef4444;
    }

    .state-box, .empty-card {
      text-align: center;
      padding: 50px 20px;
      color: var(--text-muted);
    }
    .empty-icon { font-size: 36px; margin-bottom: 12px; }
  `]
})
export class ReviewListComponent implements OnInit {
  reviews: ReviewItem[] = [];
  selectedStatus: 'all' | 'pending' | 'approved' | 'rejected' = 'all';
  searchQuery = '';
  loading = false;
  actionLoadingId: string | null = null;

  stats: ReviewStats = {
    totalReviews: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    averageRating: 5
  };

  private searchDebounceTimer: any;

  constructor(private reviewService: ReviewService) {}

  ngOnInit(): void {
    this.loadReviews();
  }

  loadReviews(): void {
    this.loading = true;
    this.reviewService.getAdminReviews(this.selectedStatus, this.searchQuery).subscribe({
      next: (res) => {
        this.reviews = res.reviews;
        this.stats = res.stats;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        console.error('Failed to load reviews:', err);
      }
    });
  }

  setStatusFilter(status: 'all' | 'pending' | 'approved' | 'rejected'): void {
    this.selectedStatus = status;
    this.loadReviews();
  }

  onSearchChange(): void {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.loadReviews();
    }, 350);
  }

  updateStatus(review: ReviewItem, status: 'approved' | 'rejected'): void {
    this.actionLoadingId = review._id;
    this.reviewService.updateReviewStatus(review._id, status).subscribe({
      next: (res) => {
        this.actionLoadingId = null;
        review.status = status;
        review.isApproved = (status === 'approved');
        // Refresh counts
        this.loadReviews();
      },
      error: (err) => {
        this.actionLoadingId = null;
        alert(err?.error?.message || 'Failed to update review status');
      }
    });
  }

  deleteReview(review: ReviewItem): void {
    if (!confirm(`Are you sure you want to delete the review by "${review.name}"?`)) {
      return;
    }

    this.actionLoadingId = review._id;
    this.reviewService.deleteReview(review._id).subscribe({
      next: () => {
        this.actionLoadingId = null;
        this.reviews = this.reviews.filter((r) => r._id !== review._id);
        this.loadReviews();
      },
      error: (err) => {
        this.actionLoadingId = null;
        alert(err?.error?.message || 'Failed to delete review');
      }
    });
  }

  getStatusClass(status: string): string {
    if (status === 'approved') return 'badge-approved';
    if (status === 'rejected') return 'badge-rejected';
    return 'badge-pending';
  }

  getStatusLabel(status: string): string {
    if (status === 'approved') return '🟢 APPROVED';
    if (status === 'rejected') return '🔴 REJECTED';
    return '🟡 PENDING APPROVAL';
  }
}
